import React, { useEffect, useState } from "react";
import SwoodleUpdatedLogo from "../../../Assets/Icons/SwoodleUpdatedLogo.svg";
import PrimaryButton from "../../common/PrimaryButton";
import TextInput from "../../common/SignUp/TextInput";
import emailIcon from "../../../Assets/Icons/emailIcon.svg";
import passwordIcon from "../../../Assets/Icons/passwordIcon.svg";
import { setLoginData } from "../../../redux/slices/authSlice";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { login } from "../../../services/authService";
import { loginSchema } from "../../../utils/validationSchemas";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { setCurrentBusiness } from "../../../redux/slices/merchantSlice";
// import { getUserSubscription } from "../../../services/subscriptionService";
import Checkbox from "../../common/Checkbox";
import { ReactComponent as UncheckedIcon } from "../../../Assets/Icons/uncheckedBlackBox.svg";
import Loader from "../../common/Loader";
import { getFcmToken } from "../../../utils/connectStore";

const DELETED_MERCHANT_MESSAGE =
  "Your Account got deleted. Please Contact support.";
const DISABLED_MERCHANT_MESSAGE =
  "Your Account is Disabled. Please contact Support for further information.";

const AccountLogin = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [showPassword, setShowPassword] = useState(false);
  const [rememberCheckbox, setRememberCheckbox] = useState(false);
  const [authErrorMessage, setAuthErrorMessage] = useState("");
  const loading = useSelector((state) => state?.onboardingReducer?.loading);
  const token = useSelector((state) => state?.authReducer?.tokenData?.token);
  const role = useSelector((state) => state?.authReducer?.tokenData?.user?.role);
  const requestedRedirect = searchParams.get("redirect");

  const getPostLoginRedirect = () => {
    const normalizedRedirect = String(requestedRedirect || "").trim();
    if (normalizedRedirect.startsWith("/merchant/")) {
      return normalizedRedirect;
    }
    return "/merchant/dashboard";
  };

  const getMerchantStatusMessage = (merchantProfiles = [], fallbackMessage = "") => {
    const profiles = Array.isArray(merchantProfiles) ? merchantProfiles : [];
    const normalizedFallbackMessage = String(fallbackMessage || "").toLowerCase();
    const normalizeStatus = (status) => String(status || "").toLowerCase();
    const isBlockedStatus = (status) => ["deleted", "disabled", "declined", "rejected"].includes(normalizeStatus(status));

    const hasActiveProfile = profiles.some((profile) => !isBlockedStatus(profile?.status));

    if (!hasActiveProfile && profiles.some((profile) => normalizeStatus(profile?.status) === "deleted")) {
      return DELETED_MERCHANT_MESSAGE;
    }

    if (!hasActiveProfile && profiles.some((profile) => ["disabled", "declined", "rejected"].includes(normalizeStatus(profile?.status)))) {
      return DISABLED_MERCHANT_MESSAGE;
    }

    if (normalizedFallbackMessage.includes("deleted")) {
      return DELETED_MERCHANT_MESSAGE;
    }

    if (normalizedFallbackMessage.includes("disabled")) {
      return DISABLED_MERCHANT_MESSAGE;
    }

    if (
      normalizedFallbackMessage ===
      "some of your merchant accounts are still in progress or require further action. please contact support."
    ) {
      return DELETED_MERCHANT_MESSAGE;
    }

    if (
      normalizedFallbackMessage ===
      "your account is not active at the moment. please contact admin."
    ) {
      return DISABLED_MERCHANT_MESSAGE;
    }

    return fallbackMessage;
  };

  useEffect(() => {
    const normalizedRole = typeof role === "string" ? role.toLowerCase() : role;

    if (token && normalizedRole === "merchant") {
      navigate(getPostLoginRedirect(), { replace: true });
    }
  }, [token, role, navigate, requestedRedirect]);

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setAuthErrorMessage("");
      try {
        const promiseResult = await dispatch(
          login({
            email: email,
            password: password,
            role: "merchant",
            fcmToken: getFcmToken(),
          })
        ).unwrap();

        if (promiseResult?.success !== true) {
          const msg = promiseResult?.message || promiseResult?.error || "";
          const merchantStatusMessage = getMerchantStatusMessage([], msg);
          const pending = typeof msg === "string" && msg.toLowerCase().includes("pending");
          if (pending) {
            // Backend shows a toast; don't show inline field errors
            return;
          }
          setAuthErrorMessage(merchantStatusMessage || "Invalid email or password");
          formik.setTouched({ ...formik.touched, password: true });
          formik.setFieldError("password", merchantStatusMessage || "Invalid email or password");
          return;
        }

        const user = promiseResult?.data?.user || promiseResult?.user;
        // Normalize role and status to lowercase to handle values like "APPROVED"/"MERCHANT"
        const userRoleRaw = user?.role;
        const userRole = typeof userRoleRaw === "string" ? userRoleRaw.toLowerCase() : userRoleRaw;

        // Block User/Member accounts
        if (userRole === "user" || userRole === "member") {
          setAuthErrorMessage("This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.");
          formik.setTouched({ email: true, password: true });
          formik.setFieldError("email", "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.");
          formik.setFieldError("password", "This email is registered as a User/Member account. User/Member accounts cannot log in here. Please use the mobile app.");
          return;
        }

        if (userRole && userRole !== "merchant") {
          setAuthErrorMessage("Invalid email or password");
          formik.setTouched({ ...formik.touched, password: true });
          formik.setFieldError("password", "Invalid email or password");
          return;
        }

        const merchantProfiles = user?.merchantProfiles;
        const merchantStatusMessage = getMerchantStatusMessage(merchantProfiles, "");
        if (merchantStatusMessage) {
          setAuthErrorMessage(merchantStatusMessage);
          formik.setTouched({ ...formik.touched, password: true });
          formik.setFieldError("password", merchantStatusMessage);
          return;
        }

        if (merchantProfiles.length > 0) {
          dispatch(setCurrentBusiness(merchantProfiles[0]));
        }

        navigate(getPostLoginRedirect());

        dispatch(
          setLoginData({
            email: email,
            password: password,
            fcmToken: getFcmToken(),
          })
        );
      } catch (err) {
        const msg = err?.response?.data?.message || err?.message || "";
        const merchantStatusMessage = getMerchantStatusMessage([], msg);
        const pending = typeof msg === "string" && msg.toLowerCase().includes("pending");
        if (!pending) {
          setAuthErrorMessage(merchantStatusMessage || "Invalid email or password");
          formik.setTouched({ ...formik.touched, password: true });
          formik.setFieldError("password", merchantStatusMessage || "Invalid email or password");
        }
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { email, password } = formik.values;

  const { handleBlur, handleSubmit, errors, touched, isSubmitting } = formik;

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleInputChange = (event) => {
    if (authErrorMessage) {
      setAuthErrorMessage("");
    }
    formik.handleChange(event);
  };

  const handleSignupClick = () => {
    navigate("/merchant/getting-started")
  };

  return (
    <form
      className="w-full py-10 h-screen flex flex-col items-center offerform"
      onSubmit={handleSubmit}
    >
      {loading && <Loader />}
      <p className="font-poppins font-[500] text-[14px] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030] px-4">
        Welcome to Swoodle! Log into your Account!
      </p>
      <div className="flex flex-col items-center w-[90%] sm:w-[70%] md:w-[60%] lg:w-[50%] xl:w-[45%] 2xl:w-[40%]">
        <img src={SwoodleUpdatedLogo} alt="Swoodle" className="pt-[6%] pb-[2%] w-[60%]"></img>
        <p className="font-poppins font-[500] sm:text-[18px] md:text-[22px] lg:text-[26px] text-[#303030]">
          MERCHANT
        </p>
        <p className="font-poppins font-[400] text-[14px] sm:text-[16px] md:text-[18px] lg:text-[18px] xl:text-[22px] text-[#303030] mt-6 sm:mt-10 mb-3 text-center">
          Your Account
        </p>
        <div className="flex flex-col justify-start items-start gap-2 w-full">
          <TextInput
            Icon={emailIcon}
            name={"email"}
            value={email}
            placeholder={"Email ID"}
            inputClass={
              "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[3%]"
            }
            iconAreaClass={
              "w-[11%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
            }
            innerDivClass={"h-[45px] sm:h-[45px] md:h-[50px]"}
            inputDivClass={"h-[70px] sm:h-[65px]"}
            handleChange={handleInputChange}
            handleBlur={handleBlur}
            error={errors.email}
            touched={touched.email}
            autoComplete={"new-password"}
          />

          <TextInput
            Icon={passwordIcon}
            type={showPassword ? "text" : "password"}
            name={"password"}
            value={password}
            placeholder={"Password"}
            inputClass={
              "w-full rounded-tr-[5px] rounded-br-[5px] border border-[#D9D9D9] pl-[3%]"
            }
            iconAreaClass={
              "w-[11%] bg-[#E6E6E6] rounded-tl-[5px] rounded-bl-[5px] border border-[#D9D9D9]"
            }
            innerDivClass={"h-[45px] sm:h-[45px] md:h-[50px]"}
            inputDivClass={"h-[70px] sm:h-[65px]"}
            handleChange={handleInputChange}
            handleBlur={handleBlur}
            error={errors.password || authErrorMessage}
            touched={touched.password}
            showPassword={showPassword}
            togglePassword={togglePassword}
            autoComplete={"new-password"}
          />
          <div className="w-full flex items-center justify-between mt-1 sm:mt-1 2xl:mt-[0%]">
            <div className=" w-[60%] flex items-center customize">
              <Checkbox
                text="Remember me"
                divClassName="flex items-center justify-center"
                checked={rememberCheckbox}
                onChecked={() => {
                  setRememberCheckbox(!rememberCheckbox);
                }}
                textClass="text-[12px] sm:text-[12px] lg:text-[14px] font-[400] font-poppins text-[#767676] ml-2 whitespace-nowrap"
                checkClassName="!w-[18px] !h-[18px] sm:!w-[20px] sm:!h-[20px] -translate-y-[2px]"
              />
            </div>

            <div className="w-[50%] text-right">
              <p className="text-[10px] sm:text-[12px] lg:text-[14px] font-[400] text-[#767676] font-poppins">
                <NavLink to="/forget-password">Forget Password</NavLink>
              </p>
            </div>
          </div>
        </div>
        <p className="text-[9px] sm:text-[12px] md:text-[14px] text-center font-poppins font-[400] mt-2 w-[95%]">
          <button
            type="button"
            className="text-[#00ADEF] font-[500] cursor-pointer hover:underline"
            onClick={() => {
              const mailto = "mailto:support@swoodle.com.au";
              try {
                const newWin = window.open(mailto, "_blank");
                if (!newWin) {
                  // Fallback: programmatically click a temporary anchor
                  const a = document.createElement("a");
                  a.href = mailto;
                  a.target = "_blank";
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                }
              } catch (_) {
                window.location.href = mailto;
              }
            }}
          >
            Need Help?
          </button>
        </p>
        <p className="text-[9px] sm:text-[12px] md:text-[14px] text-center font-poppins font-[400] mt-2 w-[95%]">
          Don't have a Merchant account?
          <span
            className="text-[#00ADEF] font-[500] cursor-pointer"
            onClick={handleSignupClick}
          >
            &nbsp; Sign up with Swoodle
          </span>
        </p>
        <div className="text-center w-full mt-4 sm:mt-5">
          <PrimaryButton
            type={"submit"}
            loading={isSubmitting}
            className={
              "w-full font-poppins text-[12px] sm:text-[14px] lg:text-[16px] text-center font-[500] h-[40px] sm:h-[45px] md:h-[50px] lg:h-[55px] rounded-[10px] mb-4 sm:mb-5 tracking-wide"
            }
            label="LOG INTO SWOODLE MERCHANT CENTRE"
          />
        </div>
      </div>
    </form>
  );
};

export default AccountLogin;
